/**
 * Data Handler Functions
 * Functions for managing data operations (add, update, delete) in the Student Portfolio App
 */

import { db } from './firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  getDocs,
  query,
  where,
  getDoc
} from 'firebase/firestore';

/**
 * Adds tag feedback to a post
 * @param {Object} params - Parameters object
 * @param {string} params.postId - The post ID
 * @param {string} params.tag - The tag/skill name
 * @param {Object} params.newTagFeedback - State object containing feedback data
 * @param {Function} params.setNewTagFeedback - State setter for feedback
 * @param {Object} params.currentUser - Current user object
 * @param {Object} params.userProfile - User profile object
 * @param {Array} params.tagFeedbacks - Existing tag feedbacks
 * @param {Array} params.posts - Array of all posts
 */
export const handleAddTagFeedback = async ({ 
  postId, 
  tag, 
  newTagFeedback, 
  setNewTagFeedback, 
  currentUser, 
  userProfile, 
  tagFeedbacks, 
  posts 
}) => {
  const key = `${postId}__${tag}`;
  const entry = newTagFeedback[key] || {};
  const comment = (entry.comment || '').trim();
  const score = Number(entry.score || 0);
  if (!currentUser || !userProfile) return;
  if (!comment && !(score >= 1 && score <= 5)) return;

  // Check if teacher has already given feedback for this skill
  const existingFeedback = tagFeedbacks.find(tf => 
    tf.postId === postId && 
    tf.tag === tag && 
    tf.teacherId === currentUser.uid
  );
  
  if (existingFeedback) {
    alert('You have already given feedback for this skill. Each teacher can only provide feedback once per skill.');
    return;
  }

  try {
    await addDoc(collection(db, 'tagFeedback'), {
      postId,
      tag,
      studentId: posts.find(p => p.id === postId)?.userId || '',
      teacherId: currentUser.uid,
      teacherName: userProfile.firstName || userProfile.name || 'Teacher',
      comment: comment || '',
      score: score >= 1 && score <= 5 ? score : null,
      timestamp: new Date().toISOString()
    });
    setNewTagFeedback({
      ...newTagFeedback,
      [key]: { comment: '', score: '' }
    });

    // Ensure the post shows this skill in its justification list so it appears everywhere
    const post = posts.find(p => p.id === postId);
    if (post) {
      const hasTag = (post.tagJustifications || []).some(tj => tj.tag === tag);
      if (!hasTag) {
        try {
          const postRef = doc(db, 'posts', postId);
          await updateDoc(postRef, {
            tagJustifications: arrayUnion({ tag, justification: 'Added by teacher for feedback' })
          });
        } catch (e) {
          console.error('Error updating post tagJustifications:', e);
        }
      }
    }
  } catch (error) {
    console.error('Error saving tag feedback:', error);
    alert('Failed to save tag feedback. Please try again.');
  }
};

/**
 * Adds a comment to a post
 * @param {Object} params - Parameters object
 * @param {string} params.postId - The post ID
 * @param {Object} params.newComment - State object containing comment data
 * @param {Function} params.setNewComment - State setter for comments
 * @param {Object} params.currentUser - Current user object
 * @param {Object} params.userProfile - User profile object
 */
export const handleAddComment = async ({ 
  postId, 
  newComment, 
  setNewComment, 
  currentUser, 
  userProfile 
}) => {
  if (!newComment[postId]?.trim() || !currentUser || !userProfile) return;

  try {
    await addDoc(collection(db, 'comments'), {
      postId: postId,
      userId: currentUser.uid,
      username: userProfile.firstName || userProfile.name,
      content: newComment[postId],
      timestamp: new Date().toISOString()
    });
    setNewComment({ ...newComment, [postId]: '' });
  } catch (error) {
    console.error("Error adding comment to Firestore:", error);
    alert('Failed to add comment. Please try again.');
  }
};

/**
 * Adds a new tag/competency to the system
 * @param {Object} params - Parameters object
 * @param {string} params.newTag - The new tag name
 * @param {Array} params.tags - Existing tags array
 * @param {Function} params.setNewTag - State setter for new tag input
 */
export const handleAddTag = async ({ newTag, tags, setNewTag }) => {
  if (!newTag.trim() || tags.includes(newTag)) return;
  const tagsDocRef = doc(db, 'settings', 'tags');
  try {
    await updateDoc(tagsDocRef, {
      list: arrayUnion(newTag)
    });
    setNewTag('');
  } catch (error) {
    console.error("Error adding tag to Firestore:", error);
    alert('Failed to add tag. Please try again.');
  }
};

/**
 * Deletes a tag/competency from the system
 * @param {Object} params - Parameters object
 * @param {string} params.tagToDelete - The tag name to delete
 * @param {Array} params.tags - Existing tags array
 * @param {Object} params.currentUser - Current user object
 */
export const handleDeleteTag = async ({ tagToDelete, tags, currentUser }) => {
  if (!currentUser) return;

  try {
    const tagsDocRef = doc(db, 'settings', 'tags');
    const updatedTags = tags.filter(tag => tag !== tagToDelete);
    await updateDoc(tagsDocRef, {
      list: updatedTags
    });
  } catch (error) {
    console.error("Error deleting tag:", error.message);
    alert('Failed to delete tag. Please try again.');
  }
};

/**
 * Updates transcript settings for the current user
 * @param {Object} params - Parameters object
 * @param {Object} params.transcriptSettings - Transcript settings object
 * @param {Object} params.currentUser - Current user object
 */
export const handleUpdateTranscriptSettings = async ({ 
  transcriptSettings, 
  currentUser 
}) => {
  if (!currentUser) return;
  
  try {
    await updateDoc(doc(db, 'users', currentUser.uid), {
      portfolioLink: transcriptSettings.portfolioLink,
      aboutMe: transcriptSettings.aboutMe,
      enrollmentStartYear: transcriptSettings.enrollmentStartYear,
      enrollmentEndYear: transcriptSettings.enrollmentEndYear
    });
  } catch (error) {
    console.error("Error updating transcript settings:", error.message);
    alert('Failed to update transcript settings. Please try again.');
  }
};

/**
 * Updates existing tag feedback
 * @param {Object} params - Parameters object
 * @param {string} params.feedbackId - The feedback document ID
 * @param {string} params.comment - Updated comment text
 * @param {number} params.score - Updated score value
 */
export const handleUpdateTagFeedback = async ({ 
  feedbackId, 
  comment, 
  score 
}) => {
  if (!feedbackId) return;
  
  try {
    const feedbackRef = doc(db, 'tagFeedback', feedbackId);
    await updateDoc(feedbackRef, {
      comment: comment || '',
      score: score >= 1 && score <= 5 ? score : null,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error("Error updating tag feedback:", error);
    alert('Failed to update feedback. Please try again.');
  }
};

/**
 * Deletes tag feedback
 * @param {Object} params - Parameters object
 * @param {string} params.feedbackId - The feedback document ID
 */
export const handleDeleteTagFeedback = async ({ feedbackId }) => {
  if (!feedbackId) return;
  
  if (!window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
    return;
  }
  
  try {
    await deleteDoc(doc(db, 'tagFeedback', feedbackId));
  } catch (error) {
    console.error("Error deleting tag feedback:", error);
    alert('Failed to delete feedback. Please try again.');
  }
};

/**
 * PROJECT MANAGEMENT DATA HANDLERS
 */

/**
 * Adds a new project
 * @param {Object} params - Parameters object
 * @param {Object} params.projectData - Project data object
 * @param {Object} params.currentUser - Current user object
 */
export const handleAddProject = async ({ projectData, currentUser }) => {
  if (!currentUser || !projectData.title?.trim()) return;

  try {
    await addDoc(collection(db, 'projects'), {
      title: projectData.title.trim(),
      description: projectData.description?.trim() || '',
      status: projectData.status || 'in-progress',
      userId: currentUser.uid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error("Error adding project:", error);
    alert('Failed to add project. Please try again.');
  }
};

/**
 * Updates an existing project
 * @param {Object} params - Parameters object
 * @param {string} params.projectId - The project document ID
 * @param {Object} params.projectData - Updated project data
 */
export const handleUpdateProject = async ({ projectId, projectData }) => {
  if (!projectId || !projectData.title?.trim()) return;

  try {
    // If project is being marked as completed, delete all its subtasks first
    if (projectData.status === 'completed') {
      const subtasksQuery = query(
        collection(db, 'subtasks'),
        where('projectId', '==', projectId)
      );
      const subtasksSnapshot = await getDocs(subtasksQuery);
      const deletePromises = subtasksSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    }

    await updateDoc(doc(db, 'projects', projectId), {
      title: projectData.title.trim(),
      description: projectData.description?.trim() || '',
      status: projectData.status || 'in-progress',
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error("Error updating project:", error);
    alert('Failed to update project. Please try again.');
  }
};

/**
 * Deletes a project and all its subtasks
 * @param {Object} params - Parameters object
 * @param {string} params.projectId - The project document ID
 */
export const handleDeleteProject = async ({ projectId }) => {
  if (!projectId) return;

  if (!window.confirm('Are you sure you want to delete this project? Remember to add the project to your reflections before deleting it.')) {
    return;
  }

  try {
    // Read project to find linked proposal (if any)
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
    const projectData = projectSnap.exists() ? projectSnap.data() : null;
    const linkedProposalId = projectData?.proposalId;

    // Delete all subtasks for this project first
    const subtasksQuery = query(
      collection(db, 'subtasks'),
      where('projectId', '==', projectId)
    );
    
    const subtasksSnapshot = await getDocs(subtasksQuery);
    const deletePromises = subtasksSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    // Delete the project
    await deleteDoc(projectRef);

    // Best-effort: also delete the linked proposal if present
    if (linkedProposalId) {
      try {
        await deleteDoc(doc(db, 'projectProposals', linkedProposalId));
      } catch (eDelProposal) {
        console.error('Error deleting linked project proposal (non-fatal):', eDelProposal);
      }
    }
  } catch (error) {
    console.error("Error deleting project:", error);
    // Fallback: if hard delete fails (e.g., permission rules), soft-archive so it no longer appears in views
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        archived: true,
        archivedAt: new Date().toISOString()
      });
      alert('You may not have permission to fully delete this project. It has been archived and will no longer appear.');
    } catch (e2) {
      console.error('Error archiving project as fallback:', e2);
      alert('Failed to delete project. Please try again.');
    }
  }
};

/**
 * Adds a new subtask to a project
 * @param {Object} params - Parameters object
 * @param {Object} params.subtaskData - Subtask data object
 * @param {string} params.projectId - The project ID
 * @param {Object} params.currentUser - Current user object
 */
export const handleAddSubtask = async ({ subtaskData, projectId, currentUser }) => {
  if (!currentUser || !projectId || !subtaskData.title?.trim()) return;

  try {
    // Get the current count of subtasks in this column to set sort order
    const subtasksQuery = query(
      collection(db, 'subtasks'),
      where('projectId', '==', projectId),
      where('status', '==', subtaskData.status || 'todo')
    );
    
    const subtasksSnapshot = await getDocs(subtasksQuery);
    const sortOrder = subtasksSnapshot.size;

    await addDoc(collection(db, 'subtasks'), {
      title: subtaskData.title.trim(),
      description: subtaskData.description?.trim() || '',
      status: subtaskData.status || 'todo',
      projectId: projectId,
      userId: currentUser.uid,
      sortOrder: sortOrder,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error("Error adding subtask:", error);
    alert('Failed to add subtask. Please try again.');
  }
};

/**
 * Updates an existing subtask
 * @param {Object} params - Parameters object
 * @param {string} params.subtaskId - The subtask document ID
 * @param {Object} params.subtaskData - Updated subtask data
 */
export const handleUpdateSubtask = async ({ subtaskId, subtaskData }) => {
  if (!subtaskId || !subtaskData.title?.trim()) return;

  try {
    await updateDoc(doc(db, 'subtasks', subtaskId), {
      title: subtaskData.title.trim(),
      description: subtaskData.description?.trim() || '',
      status: subtaskData.status || 'todo',
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error("Error updating subtask:", error);
    alert('Failed to update subtask. Please try again.');
  }
};

/**
 * Moves a subtask between columns (changes status)
 * @param {Object} params - Parameters object
 * @param {string} params.subtaskId - The subtask document ID
 * @param {string} params.newStatus - The new status/column
 * @param {string} params.projectId - The project ID
 */
export const handleMoveSubtask = async ({ subtaskId, newStatus, projectId }) => {
  if (!subtaskId || !newStatus || !projectId) return;

  try {
    // Always set sortOrder to a high number to ensure subtasks go to the bottom
    const newSortOrder = Date.now(); // Use timestamp to ensure unique ordering

    await updateDoc(doc(db, 'subtasks', subtaskId), {
      status: newStatus,
      sortOrder: newSortOrder,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error("Error moving subtask:", error);
    alert('Failed to move subtask. Please try again.');
  }
};

/**
 * Deletes a subtask
 * @param {Object} params - Parameters object
 * @param {string} params.subtaskId - The subtask document ID
 */
export const handleDeleteSubtask = async ({ subtaskId }) => {
  if (!subtaskId) return;

  if (!window.confirm('Are you sure you want to delete this subtask?')) {
    return;
  }

  try {
    await deleteDoc(doc(db, 'subtasks', subtaskId));
  } catch (error) {
    console.error("Error deleting subtask:", error);
    alert('Failed to delete subtask. Please try again.');
  }
};

/**
 * Adds a new project proposal
 * @param {Object} params - Parameters object
 * @param {Object} params.proposalData - Project proposal data object
 * @param {Object} params.currentUser - Current user object
 * @param {Object} params.userProfile - User profile object
 */
export const handleAddProjectProposal = async ({ proposalData, currentUser, userProfile }) => {
  if (!currentUser || !proposalData.title?.trim()) return;

  try {
    await addDoc(collection(db, 'projectProposals'), {
      title: proposalData.title.trim(),
      description: proposalData.description?.trim() || '',
      objectives: proposalData.objectives?.trim() || '',
      timeline: proposalData.timeline?.trim() || '',
      resources: proposalData.resources?.trim() || '',
      memberIds: Array.isArray(proposalData.memberIds) ? proposalData.memberIds : [],
      studentId: currentUser.uid,
      studentName: userProfile?.name || 'Unknown Student',
      studentEmail: currentUser.email,
      status: 'pending',
      timestamp: new Date().toISOString(),
      approvedAt: null,
      approvedBy: null
    });
  } catch (error) {
    console.error('Error adding project proposal:', error);
    alert('Failed to submit project proposal. Please try again.');
  }
};

/**
 * Approves a project proposal and creates the actual project
 * @param {Object} params - Parameters object
 * @param {string} params.proposalId - The proposal document ID
 * @param {Object} params.currentUser - Current user object
 * @param {Object} params.userProfile - User profile object
 */
export const handleApproveProjectProposal = async ({ proposalId, currentUser, userProfile }) => {
  if (!proposalId || !currentUser || !userProfile) return;

  try {
    // First, get the proposal data
    const proposalRef = doc(db, 'projectProposals', proposalId);
    const proposalSnap = await getDoc(proposalRef);
    
    if (!proposalSnap.exists()) {
      alert('Proposal not found.');
      return;
    }

    const proposalData = proposalSnap.data();

    // Update the proposal status
    await updateDoc(proposalRef, {
      status: 'approved',
      approvedAt: new Date().toISOString(),
      approvedBy: currentUser.uid,
      approvedByName: userProfile.firstName || userProfile.name || 'Teacher'
    });

    // Create the actual project
    await addDoc(collection(db, 'projects'), {
      title: proposalData.title,
      description: proposalData.description,
      objectives: proposalData.objectives || '',
      timeline: proposalData.timeline || '',
      resources: proposalData.resources || '',
      status: 'in-progress',
      userId: proposalData.studentId, // legacy owner field
      ownerId: proposalData.studentId,
      isGroup: (proposalData.memberIds || []).length > 0,
      memberIds: proposalData.memberIds || [],
      participantIds: [proposalData.studentId, ...((proposalData.memberIds || []).filter(Boolean))],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      proposalId: proposalId // Link back to the original proposal
    });

    // After successful project creation, remove the proposal since it is no longer used
    try {
      await deleteDoc(proposalRef);
    } catch (deleteErr) {
      // Non-fatal: if delete fails due to rules or transient issues, keep going
      console.error('Error deleting approved project proposal (non-fatal):', deleteErr);
    }

  } catch (error) {
    console.error('Error approving project proposal:', error);
    alert('Failed to approve project proposal. Please try again.');
  }
};

/**
 * Rejects a project proposal
 * @param {Object} params - Parameters object
 * @param {string} params.proposalId - The proposal document ID
 * @param {Object} params.currentUser - Current user object
 * @param {Object} params.userProfile - User profile object
 */
export const handleRejectProjectProposal = async ({ proposalId, currentUser, userProfile }) => {
  if (!proposalId || !currentUser || !userProfile) return;

  try {
    const proposalRef = doc(db, 'projectProposals', proposalId);
    await updateDoc(proposalRef, {
      status: 'rejected',
      approvedAt: new Date().toISOString(),
      approvedBy: currentUser.uid,
      approvedByName: userProfile.firstName || userProfile.name || 'Teacher'
    });
  } catch (error) {
    console.error('Error rejecting project proposal:', error);
    alert('Failed to reject project proposal. Please try again.');
  }
};

/**
 * Updates a project proposal
 * @param {Object} params - Parameters object
 * @param {string} params.proposalId - The proposal document ID
 * @param {Object} params.proposalData - The updated proposal data
 */
export const handleUpdateProjectProposal = async ({ proposalId, proposalData }) => {
  if (!proposalId || !proposalData) return;

  try {
    const proposalRef = doc(db, 'projectProposals', proposalId);
    await updateDoc(proposalRef, {
      title: proposalData.title?.trim() || '',
      description: proposalData.description?.trim() || '',
      objectives: proposalData.objectives?.trim() || '',
      timeline: proposalData.timeline?.trim() || '',
      resources: proposalData.resources?.trim() || ''
    });
  } catch (error) {
    console.error('Error updating project proposal:', error);
    alert('Failed to update project proposal. Please try again.');
  }
};

/**
 * Deletes a project proposal
 * @param {Object} params - Parameters object
 * @param {string} params.proposalId - The proposal document ID
 */
export const handleDeleteProjectProposal = async ({ proposalId }) => {
  if (!proposalId) return;

  if (!window.confirm('Are you sure you want to delete this project proposal?')) {
    return;
  }

  try {
    await deleteDoc(doc(db, 'projectProposals', proposalId));
  } catch (error) {
    console.error('Error deleting project proposal:', error);
    alert('Failed to delete project proposal. Please try again.');
  }
};

/**
 * Generates a new shareable transcript link for a student
 * @param {Object} params - Parameters object
 * @param {string} params.studentId - The student's user ID
 * @param {Object} params.currentUser - Current user object
 */
export const handleGenerateShareableTranscript = async ({ studentId, currentUser }) => {
  if (!studentId || !currentUser || currentUser.uid !== studentId) {
    alert('You can only generate shareable links for your own transcript.');
    return null;
  }

  try {
    // Generate a unique token
    const token = crypto.randomUUID();
    const shareableLink = {
      studentId: studentId,
      token: token,
      createdAt: new Date().toISOString(),
      isActive: true,
      accessCount: 0,
      lastAccessedAt: null
    };

    // Store in Firestore
    await addDoc(collection(db, 'shareableTranscripts'), shareableLink);

    // Return the public URL
    const publicUrl = `${window.location.origin}${window.location.pathname}?transcript=${token}`;
    return { token, url: publicUrl };
  } catch (error) {
    console.error('Error generating shareable transcript:', error);
    alert('Failed to generate shareable link. Please try again.');
    return null;
  }
};

/**
 * Fetches a shareable transcript by token
 * @param {Object} params - Parameters object
 * @param {string} params.token - The shareable transcript token
 */
export const handleGetShareableTranscript = async ({ token }) => {
  if (!token) return null;

  try {
    const q = query(
      collection(db, 'shareableTranscripts'),
      where('token', '==', token),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = { id: doc.id, ...doc.data() };

    // Try to update access count and last accessed time
    // This will fail for unauthenticated users, but that's okay - transcript is still viewable
    try {
      await updateDoc(doc.ref, {
        accessCount: (data.accessCount || 0) + 1,
        lastAccessedAt: new Date().toISOString()
      });
    } catch (updateError) {
      // Silently fail - access tracking is optional for public views
      console.warn('Could not update access count (this is normal for unauthenticated users):', updateError);
    }

    return { ...data, accessCount: (data.accessCount || 0) + 1 };
  } catch (error) {
    console.error('Error fetching shareable transcript:', error);
    return null;
  }
};

/**
 * Revokes a shareable transcript link
 * @param {Object} params - Parameters object
 * @param {string} params.token - The shareable transcript token
 * @param {string} params.studentId - The student's user ID
 * @param {Object} params.currentUser - Current user object
 */
export const handleRevokeShareableTranscript = async ({ token, studentId, currentUser }) => {
  if (!token || !studentId || !currentUser || currentUser.uid !== studentId) {
    alert('You can only revoke your own shareable links.');
    return;
  }

  try {
    const q = query(
      collection(db, 'shareableTranscripts'),
      where('token', '==', token),
      where('studentId', '==', studentId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      alert('Shareable link not found.');
      return;
    }

    const docRef = querySnapshot.docs[0].ref;
    await deleteDoc(docRef);

  } catch (error) {
    console.error('Error revoking shareable transcript:', error);
    alert('Failed to revoke shareable link. Please try again.');
  }
};

/**
 * Fetches all active shareable transcripts for a student
 * @param {Object} params - Parameters object
 * @param {string} params.studentId - The student's user ID
 */
export const handleGetStudentShareableTranscripts = async ({ studentId }) => {
  if (!studentId) return [];

  try {
    const q = query(
      collection(db, 'shareableTranscripts'),
      where('studentId', '==', studentId),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      publicUrl: `${window.location.origin}${window.location.pathname}?transcript=${doc.data().token}`
    }));
  } catch (error) {
    console.error('Error fetching student shareable transcripts:', error);
    return [];
  }
};

